import { fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { AclModel, apiSlice, UserReadModel } from '@client/api';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  getSessionState,
  SessionState,
  setSession,
  setSessionLanguage,
  setSessionTimezone,
  setSessionUser,
} from './session.slice';

type Metadata = {
  arg?: {
    originalArgs?: {
      id?: string;
    };
  };
};

const logger = (msg: string, ...params: unknown[]) => {
  console.debug('[SESSION]', msg, ...params);
};

function* handleGetApiAuthSessionFulfilled(action: PayloadAction<AclModel>) {
  logger('⤴️ Init session started');

  logger('👤 Set session user');
  yield put(setSession(action.payload));

  logger('🇺🇳 Set language of session user');
  try {
    // yield put(setSessionLanguage(action.payload?.user?.person?.culture?.language ?? null));
    yield put(setSessionLanguage('no')); // ignore setting from BE, hardcoded to no for now
  } catch (error) {
    logger('❎ Could not set session language - Continuing running app', { error });
  }

  logger('🌎 Set timezone of session user');
  try {
    yield put(setSessionTimezone(action.payload?.user?.person?.culture?.timezone ?? null));
  } catch (error) {
    logger('❎ Could not set session timezone - Continuing running app', { error });
  }

  logger('✅ Init session succeeded');
}

function* handleGetApiAuthSessionUserFulfilled(action: PayloadAction<UserReadModel>) {
  logger('🔄 Update session user started');

  logger('👤 Set updated session user');
  yield put(setSessionUser(action.payload));

  logger('🇺🇳 Set language of session user');
  try {
    yield put(setSessionLanguage(action.payload?.person?.culture?.language ?? null));
  } catch (error) {
    logger('❎ Could not set session language - Continuing running app', { error });
  }

  logger('🌎 Set timezone of session user');
  try {
    yield put(setSessionTimezone(action.payload?.person?.culture?.timezone ?? null));
  } catch (error) {
    logger('❎ Could not set session timezone - Continuing running app', { error });
  }

  logger('✅ Update session user succeeded');
}

// handler for checking if the session person has changed and needs to be updated
function* handlePutApiPersonByIdFulfilled(action: PayloadAction<null, string, Metadata>) {
  const personId = action?.meta?.arg?.originalArgs?.id;
  if (personId == null) return;

  const sessionState: SessionState = yield select(getSessionState);
  const { user } = sessionState;
  const sessionPersonId = user?.person?.id;
  if (sessionPersonId == null) return;

  if (personId !== sessionPersonId) return;

  // make sure that the `putPersonsById` api call is invalidating the cache for `getAuthSessionUser` otherwise it will cache previous result (= skips fetching)
  yield put(apiSlice.endpoints.getAuthSessionUser.initiate() as never);
}

function* watchGetApiAuthSessionFulfilled() {
  const matcher = apiSlice.endpoints.getAuthSession?.matchFulfilled;
  if (!matcher) return;
  yield takeEvery(matcher, handleGetApiAuthSessionFulfilled);
}

function* watchGetApiAuthSessionUserFulfilled() {
  const matcher = apiSlice.endpoints.getAuthSessionUser?.matchFulfilled;
  if (!matcher) return;
  yield takeLatest(matcher, handleGetApiAuthSessionUserFulfilled);
}

function* watchPutApiPersonsById() {
  const matcher = apiSlice.endpoints.putPersonsById?.matchFulfilled;
  if (!matcher) return;
  yield takeEvery(matcher, handlePutApiPersonByIdFulfilled);
}

function* startSessionWatchers() {
  yield fork(watchGetApiAuthSessionFulfilled);
  yield fork(watchGetApiAuthSessionUserFulfilled);
  yield fork(watchPutApiPersonsById);
}

export function* sessionSaga() {
  yield fork(startSessionWatchers);
}
